import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Clock, Loader2, Send, Sparkles, MessageSquare, X, AlertCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/Skeleton";
import { Textarea } from "@/components/ui/textarea";
import { AvatarPortrait } from "@/components/AvatarPortrait";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ChatMessage, TypingIndicator, ConnectionBanner } from "@/components/chat/ChatMessage";
import { NotesPanel } from "@/components/chat/NotesPanel";
import { formatMMSS } from "@/lib/sessionHelpers";
import { cn } from "@/lib/utils";

const RESYNC_INTERVAL_MS = 60_000;
const MAX_MESSAGE_LEN = 4000;

const STATUS_LABEL = {
    draft: "Draft",
    active: "Active",
    completed: "Completed",
    reviewed: "Reviewed",
    failed: "Failed",
    ending: "Ending…",
};

export default function PracticeSessionPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [session, setSession] = useState(null); // server doc
    const [fetchError, setFetchError] = useState(null);
    const [conversation, setConversation] = useState([]); // augmented with _key + local-only states
    const [pendingTurn, setPendingTurn] = useState(null); // {key, content}
    const [pendingError, setPendingError] = useState(null);
    const [draft, setDraft] = useState("");
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [endDialogOpen, setEndDialogOpen] = useState(false);
    const [endWarn, setEndWarn] = useState(null);
    const [isEnding, setIsEnding] = useState(false);
    const [connectionWarning, setConnectionWarning] = useState(false);

    const lastTurnRetryRef = useRef(null);
    const tickRef = useRef(null);
    const resyncRef = useRef(null);

    // ------- decorate raw conversation array with stable keys -------
    const decorateConversation = useCallback((convo) => {
        return convo.map((t, i) => ({ ...t, _key: `${i}-${t.role}-${t.timestamp || ""}` }));
    }, []);

    // ------- initial fetch + auto-start if draft -------
    const loadSession = useCallback(async (autoStart = true) => {
        try {
            const res = await api.get(`/sessions/${id}`);
            let s = res.data;
            if (s.status === "draft" && autoStart) {
                const startRes = await api.post(`/sessions/${id}/start`);
                s = startRes.data;
            }
            setSession(s);
            setConversation(decorateConversation(s.conversation || []));
            setTimeRemaining(s.time_remaining_seconds);
            setConnectionWarning(false);
            return s;
        } catch (err) {
            if (err?.response?.status === 404) {
                setFetchError("Session not found");
            } else {
                setConnectionWarning(true);
            }
            return null;
        }
    }, [id, decorateConversation]);

    useEffect(() => {
        loadSession(true);
    }, [loadSession]);

    // ------- local 1Hz timer -------
    useEffect(() => {
        if (session?.status !== "active" || timeRemaining === null) return undefined;
        tickRef.current = setInterval(() => {
            setTimeRemaining((tr) => {
                if (tr === null || tr === undefined) return tr;
                if (tr <= 1) {
                    // timer ran out — trigger auto-end
                    clearInterval(tickRef.current);
                    handleTimerExpiry();
                    return 0;
                }
                return tr - 1;
            });
        }, 1000);
        return () => clearInterval(tickRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session?.status]);

    // ------- 60s server resync + visibilitychange resync -------
    useEffect(() => {
        if (session?.status !== "active") return undefined;
        resyncRef.current = setInterval(async () => {
            await quietResync();
        }, RESYNC_INTERVAL_MS);
        const onVis = () => {
            if (document.visibilityState === "visible") quietResync();
        };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            clearInterval(resyncRef.current);
            document.removeEventListener("visibilitychange", onVis);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session?.status]);

    const quietResync = useCallback(async () => {
        try {
            const res = await api.get(`/sessions/${id}`);
            const s = res.data;
            setSession(s);
            setConversation(decorateConversation(s.conversation || []));
            setTimeRemaining(s.time_remaining_seconds);
            setConnectionWarning(false);
            if (s.status === "completed" || s.status === "reviewed") {
                // server-side auto-finalize caught up to us
                clearInterval(tickRef.current);
            }
        } catch {
            setConnectionWarning(true);
        }
    }, [id, decorateConversation]);

    const handleTimerExpiry = useCallback(async () => {
        try {
            const res = await api.post(`/sessions/${id}/end`);
            setSession(res.data);
            setConversation(decorateConversation(res.data.conversation || []));
            toast.info("Time is up — session ended");
            if (res.data.warn === "too_short_for_review") setEndWarn(res.data.warn);
        } catch {
            // server may already have auto-finalized; refetch
            await loadSession(false);
            toast.info("Time is up — session ended");
        }
    }, [id, loadSession, decorateConversation]);

    // ------- send a turn -------
    const sendTurn = useCallback(async (text) => {
        const trimmed = text.trim();
        if (!trimmed || pendingTurn) return;
        const key = `pending-${Date.now()}`;
        setPendingTurn({ _key: key, role: "counsellor", content: trimmed, timestamp: new Date().toISOString() });
        setPendingError(null);
        setDraft("");
        lastTurnRetryRef.current = trimmed;

        try {
            const res = await api.post(`/sessions/${id}/turn`, { counsellor_message: trimmed });
            // Server now has both turns persisted; refresh from response data
            setConversation((cur) => [
                ...cur,
                { _key: `s-${Date.now()}-c`, role: "counsellor", content: trimmed, timestamp: res.data.server_time },
                { _key: `s-${Date.now()}-r`, role: "client", content: res.data.client_reply, timestamp: res.data.server_time },
            ]);
            setPendingTurn(null);
            setPendingError(null);
            if (res.data.time_remaining_seconds !== undefined) {
                setTimeRemaining(res.data.time_remaining_seconds);
            }
            lastTurnRetryRef.current = null;
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 409 && typeof detail === "object" && detail?.error === "session_expired") {
                toast.info("Time is up — session ended");
                setPendingTurn(null);
                await loadSession(false);
                return;
            }
            if (status === 502 && typeof detail === "object" && detail?.retryable) {
                setPendingError("Tap to retry");
                // keep pendingTurn so the message stays visible with a retry button
                return;
            }
            // Unknown / connection error
            setPendingError("Tap to retry");
            setConnectionWarning(true);
        }
    }, [id, pendingTurn, loadSession]);

    const retryLastTurn = useCallback(() => {
        if (!lastTurnRetryRef.current) return;
        const text = lastTurnRetryRef.current;
        setPendingTurn(null);
        setPendingError(null);
        sendTurn(text);
    }, [sendTurn]);

    // ------- end session -------
    const confirmEnd = useCallback(async () => {
        if (isEnding) return; // guard against double-submit
        setEndDialogOpen(false);
        setIsEnding(true);
        // Stop the local timer + resync polling while we end — they'd otherwise
        // race the slow /end call and flip status mid-flight.
        clearInterval(tickRef.current);
        clearInterval(resyncRef.current);
        try {
            const res = await api.post(`/sessions/${id}/end`);
            setSession(res.data);
            setConversation(decorateConversation(res.data.conversation || []));
            const reviewStatus = res.data?.review?.status;
            if (res.data.warn === "too_short_for_review" || reviewStatus === "skipped_too_short") {
                setEndWarn("too_short_for_review");
                toast.success("Session ended");
                setIsEnding(false);
                return;
            }
            toast.success("Session ended — opening review");
            navigate(`/practice/review/${id}`);
        } catch (err) {
            // 409 "Cannot end a session with status 'completed'" means the
            // session already ended on the server (auto-finalize or a prior
            // request that succeeded). Treat as success — refetch + route.
            const status = err?.response?.status;
            const raw = err?.response?.data?.detail;
            const detailStr = typeof raw === "string"
                ? raw
                : raw && typeof raw === "object" ? (raw.message || raw.error || "") : "";

            if (status === 409 && /completed|reviewed/i.test(detailStr)) {
                const refreshed = await loadSession(false);
                const reviewStatus = refreshed?.review?.status;
                if (reviewStatus === "skipped_too_short" || (refreshed && (refreshed.conversation || []).filter((t) => t.role === "client").length < 2)) {
                    setEndWarn("too_short_for_review");
                    toast.success("Session ended");
                } else {
                    toast.success("Session ended — opening review");
                    navigate(`/practice/review/${id}`);
                }
                setIsEnding(false);
                return;
            }

            // Real failure — Bug 1 fix: coerce structured detail to string so
            // Sonner can render it without crashing the React tree.
            let msg = "Could not end session — please try again";
            if (typeof raw === "string") msg = raw;
            else if (raw && typeof raw === "object") msg = raw.message || raw.error || msg;
            toast.error(msg);
            setIsEnding(false);
        }
    }, [id, isEnding, decorateConversation, navigate, loadSession]);

    // Build transcript items — must be before any early return to satisfy
    // the rules-of-hooks. Safe to read session-derived fields with optional chaining.
    const transcriptItems = useMemo(() => {
        const avatarName = session?.avatar_name;
        const avatarImageUrl = session?.avatar_image_url;
        const counsellorName = session?.counsellor_display_name;
        const isCouple = !!session?.is_couple;
        const list = conversation.map((t) => ({
            role: t.role,
            element: (
                <ChatMessage
                    key={t._key}
                    turn={t}
                    avatarName={avatarName}
                    avatarImageUrl={avatarImageUrl}
                    counsellorName={counsellorName}
                    isCouple={isCouple}
                />
            ),
        }));
        if (pendingTurn) {
            list.push({
                role: pendingTurn.role,
                element: (
                    <ChatMessage
                        key={pendingTurn._key}
                        turn={pendingTurn}
                        avatarName={avatarName}
                        avatarImageUrl={avatarImageUrl}
                        counsellorName={counsellorName}
                        isCouple={isCouple}
                        sendError={pendingError}
                        isSending={!pendingError}
                        onRetry={retryLastTurn}
                    />
                ),
            });
        }
        return list;
    }, [conversation, pendingTurn, pendingError, retryLastTurn, session?.avatar_name, session?.avatar_image_url, session?.counsellor_display_name, session?.is_couple]);

    // -------------------- render branches --------------------
    if (fetchError) {
        return (
            <Layout>
                <div className="mx-auto max-w-xl py-12 text-center" data-testid="session-error">
                    <h1 className="text-2xl font-semibold text-foreground">{fetchError}</h1>
                    <Button asChild variant="outline" className="mt-6">
                        <Link to="/">Back to home</Link>
                    </Button>
                </div>
            </Layout>
        );
    }
    if (!session) {
        return (
            <Layout>
                <div className="grid gap-6 md:grid-cols-[260px_1fr]" data-testid="session-loading">
                    <Skeleton className="aspect-square w-full rounded-2xl" />
                    <div className="space-y-3"><Skeleton className="h-6 w-2/3" /><Skeleton className="h-40 w-full" /></div>
                </div>
            </Layout>
        );
    }

    const isCompleted = session.status === "completed" || session.status === "reviewed";
    const isActive = session.status === "active";
    const composerDisabled = !isActive || !!pendingTurn;

    return (
        <Layout>
            <div className="mx-auto w-full max-w-7xl">
                <div className="grid gap-6 md:grid-cols-[240px_minmax(0,1fr)_280px]" data-testid="session-grid">
                    {/* LEFT RAIL — avatar + meta. On mobile becomes a compact header row. */}
                    <aside className="flex flex-row items-start gap-3 md:flex-col md:gap-4" data-testid="session-left-rail">
                        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl border border-border md:h-auto md:w-full md:rounded-2xl">
                            <AvatarPortrait name={session.avatar_name} imageUrl={session.avatar_image_url} aspect={session.is_couple ? "wide" : "square"} rounded="rounded-none" />
                        </div>
                        <div className="min-w-0 flex-1 md:flex-initial">
                            <div className="space-y-1">
                                <h2 className="truncate text-base font-semibold text-foreground" data-testid="session-avatar-name">{session.avatar_name}</h2>
                                <p className="truncate text-xs text-muted-foreground">Counsellor: <span className="font-medium text-foreground">{session.counsellor_display_name}</span></p>
                            </div>
                            <div className="mt-3 hidden space-y-2 text-xs md:block">
                                <MetaRow label="Therapy" value={session.therapy_type_name} testId="session-meta-therapy" />
                                <MetaRow label="Case" value={session.case_title} testId="session-meta-case" />
                                <MetaRow label="Duration" value={`${session.duration_minutes} minutes`} testId="session-meta-duration" />
                            </div>
                            <div className="mt-2 md:mt-3">
                                <StatusPill status={isEnding ? "ending" : session.status} />
                            </div>
                        </div>
                    </aside>

                    {/* CENTER — chat (single render, used on every viewport) */}
                    <main className="flex flex-col">
                        <ChatPane
                            session={session}
                            timeRemaining={timeRemaining}
                            transcriptItems={transcriptItems}
                            pendingTurn={pendingTurn}
                            connectionWarning={connectionWarning}
                            onRetryResync={quietResync}
                            draft={draft}
                            setDraft={setDraft}
                            sendTurn={sendTurn}
                            composerDisabled={composerDisabled || isEnding}
                            onEndClick={() => setEndDialogOpen(true)}
                            isCompleted={isCompleted}
                            isEnding={isEnding}
                            endWarn={endWarn}
                        />
                    </main>

                    {/* RIGHT RAIL — notes panel. On mobile stacks below chat. */}
                    <aside data-testid="session-right-rail">
                        <NotesPanel sessionId={session.id} initialNotes={session.student_notes} locked={isCompleted} />
                    </aside>
                </div>
            </div>

            {/* End session confirm dialog */}
            <AlertDialog open={endDialogOpen} onOpenChange={(open) => !isEnding && setEndDialogOpen(open)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>End this session?</AlertDialogTitle>
                        <AlertDialogDescription>
                            You won&apos;t be able to send any more messages once you end. We&apos;ll save the transcript and generate a supervisor review — this can take up to a minute.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="end-dialog-cancel" disabled={isEnding}>Stay in session</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmEnd} disabled={isEnding} data-testid="end-dialog-confirm" className="bg-destructive hover:bg-destructive/90">
                            {isEnding ? (<><Loader2 className="h-4 w-4 animate-spin" />Ending…</>) : "End session"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Full-page overlay while /end is in flight — review can take ~45s.
                Without this the user thinks the button did nothing and re-clicks. */}
            {isEnding && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-background/85 backdrop-blur-sm"
                    data-testid="ending-session-overlay"
                    role="status"
                    aria-live="polite"
                >
                    <div className="mx-4 max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-xl">
                        <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
                        <h2 className="mt-4 text-lg font-semibold text-foreground">Ending session…</h2>
                        <p className="mt-2 text-sm text-muted-foreground">
                            Saving the transcript and generating your supervisor review. This usually takes 30–60 seconds — please don&apos;t close the tab.
                        </p>
                    </div>
                </div>
            )}
        </Layout>
    );
}

// ============================================================================
// Sub-components
// ============================================================================
const MetaRow = ({ label, value, testId }) => (
    <div className="flex flex-col gap-0.5">
        <span className="text-[0.65rem] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className="text-foreground" data-testid={testId}>{value}</span>
    </div>
);

const StatusPill = ({ status }) => {
    const tone = status === "active" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
        : status === "completed" || status === "reviewed" ? "bg-muted text-muted-foreground border-border"
        : "bg-amber-100 text-amber-800 border-amber-200";
    return (
        <span
            className={cn("inline-flex w-fit items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium", tone)}
            data-testid="session-status-pill"
        >
            <span className={cn("h-1.5 w-1.5 rounded-full", status === "active" ? "bg-emerald-500" : "bg-muted-foreground")} />
            {STATUS_LABEL[status]}
        </span>
    );
};

const ChatPane = ({
    session, timeRemaining, transcriptItems, pendingTurn, connectionWarning, onRetryResync,
    draft, setDraft, sendTurn, composerDisabled, onEndClick, isCompleted, isEnding, endWarn,
}) => {
    const scrollerRef = useRef(null);

    useEffect(() => {
        const el = scrollerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [transcriptItems.length, pendingTurn]);

    return (
        <div className="flex flex-col gap-3" data-testid="chat-pane">
            {/* sticky top bar */}
            {!isCompleted && (
                <div className="sticky top-16 z-10 flex items-center justify-between gap-3 rounded-xl border border-border bg-background/85 px-4 py-2.5 backdrop-blur" data-testid="chat-topbar">
                    <SessionTimer timeRemaining={timeRemaining} />
                    <span className="text-xs text-muted-foreground" data-testid="chat-turn-count">
                        {transcriptItems.filter((t) => t.role === "counsellor").length} {transcriptItems.filter((t) => t.role === "counsellor").length === 1 ? "turn" : "turns"}
                    </span>
                    <Button
                        variant={timeRemaining !== null && timeRemaining < 120 ? "destructive" : "outline"}
                        size="sm"
                        onClick={onEndClick}
                        disabled={isEnding}
                        data-testid="end-session-button"
                    >
                        {isEnding ? (
                            <><Loader2 className="h-4 w-4 animate-spin" />Ending…</>
                        ) : (
                            <><X className="h-4 w-4" />End session</>
                        )}
                    </Button>
                </div>
            )}

            {isCompleted && <CompletedBanner warn={endWarn} sessionId={session.id} reviewStatus={session.review?.status} />}

            {connectionWarning && <ConnectionBanner onRetry={onRetryResync} />}

            <div
                ref={scrollerRef}
                className="flex max-h-[60vh] min-h-[400px] flex-col gap-4 overflow-y-auto rounded-xl bg-secondary/40 px-3 py-4 sm:px-5"
                data-testid="chat-transcript"
            >
                {transcriptItems.length === 0 && !pendingTurn && (
                    <div className="my-auto text-center text-sm text-muted-foreground" data-testid="chat-empty-state">
                        Start the session — how would you like to open?
                    </div>
                )}
                {transcriptItems.map((item) => item.element)}
                {pendingTurn && !pendingTurn.error && (
                    <TypingIndicator
                        avatarName={session.avatar_name}
                        avatarImageUrl={session.avatar_image_url}
                        isCouple={session.is_couple}
                    />
                )}
            </div>

            {!isCompleted && (
                <Composer
                    draft={draft}
                    setDraft={setDraft}
                    onSend={() => sendTurn(draft)}
                    disabled={composerDisabled}
                    isSending={!!pendingTurn && !pendingTurn.error}
                />
            )}
        </div>
    );
};

const Composer = ({ draft, setDraft, onSend, disabled, isSending }) => {
    const onKeyDown = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            onSend();
        }
    };
    const tooLong = draft.length > MAX_MESSAGE_LEN;
    return (
        <div className="flex flex-col gap-2 rounded-xl border border-border bg-surface p-3" data-testid="composer">
            <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Say something to the client… (⌘/Ctrl + Enter to send)"
                disabled={disabled}
                className="min-h-[80px] resize-none border-0 bg-transparent focus-visible:ring-0"
                data-testid="composer-textarea"
                maxLength={MAX_MESSAGE_LEN + 200}
            />
            <div className="flex items-center justify-between">
                <span className={cn("text-xs", tooLong ? "text-destructive" : "text-muted-foreground")} data-testid="composer-char-count">
                    {draft.length}/{MAX_MESSAGE_LEN}
                </span>
                <Button onClick={onSend} disabled={disabled || !draft.trim() || tooLong} className="bg-primary hover:bg-primary-hover" data-testid="composer-send-button">
                    {isSending ? (<><Loader2 className="h-4 w-4 animate-spin" />Sending…</>) : (<><Send className="h-4 w-4" />Send</>)}
                </Button>
            </div>
        </div>
    );
};

const SessionTimer = ({ timeRemaining }) => {
    const warn = timeRemaining !== null && timeRemaining < 120;
    return (
        <div
            className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium tabular-nums",
                warn ? "bg-destructive/10 text-destructive" : "bg-primary/5 text-primary"
            )}
            data-testid="session-timer"
        >
            <Clock className="h-4 w-4" />
            <span data-testid="session-timer-value">{formatMMSS(timeRemaining)}</span>
        </div>
    );
};

const CompletedBanner = ({ warn, sessionId, reviewStatus }) => (
    <div className="rounded-xl border border-border bg-surface p-5" data-testid="completed-banner">
        <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <h2 className="text-base font-semibold text-foreground">Session ended</h2>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
            {reviewStatus === "complete" || reviewStatus === "pending" || reviewStatus === "failed"
                ? "Your supervisor review is ready to view."
                : warn === "too_short_for_review"
                    ? "This session was too short to generate a supervisor review."
                    : "Your full transcript is saved."}
        </p>
        {warn === "too_short_for_review" && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800" data-testid="completed-warn-too-short">
                <AlertCircle className="h-3.5 w-3.5" />
                The session ended before there was enough conversation to review meaningfully.
            </div>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
            {(reviewStatus === "complete" || reviewStatus === "pending" || reviewStatus === "failed") && (
                <Button asChild size="sm" className="bg-primary hover:bg-primary-hover" data-testid="completed-view-review">
                    <Link to={`/practice/review/${sessionId}`}>
                        View review
                        <Sparkles className="h-3 w-3" />
                    </Link>
                </Button>
            )}
            <Button asChild variant="outline" size="sm" data-testid="completed-back-home">
                <Link to="/"><ArrowLeft className="h-4 w-4" />Back to home</Link>
            </Button>
        </div>
    </div>
);

// Memoised transcript item builder is now inlined inside the component body
// to satisfy react-hooks/rules-of-hooks (must run unconditionally on every render).
